import liff from '@line/liff'

let initPromise: Promise<void> | null = null
let lastInitError: string | null = null

export type LiffProfile = {
  userId: string
  displayName: string
  pictureUrl?: string
  idToken?: string
}

export class LiffInitError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LiffInitError'
  }
}

export function getLiffId(): string {
  return import.meta.env.VITE_LIFF_ID ?? ''
}

export function getLastLiffInitError(): string | null {
  return lastInitError
}

export async function initLiff(): Promise<void> {
  const liffId = getLiffId().trim()
  if (!liffId) {
    console.warn('VITE_LIFF_ID is not set — LIFF features are limited outside LINE')
    return
  }

  if (!initPromise) {
    initPromise = liff
      .init({ liffId, withLoginOnExternalBrowser: true })
      .then(() => {
        lastInitError = null
      })
      .catch((err: unknown) => {
        initPromise = null
        const msg =
          err instanceof Error
            ? err.message
            : 'ไม่สามารถเชื่อมต่อ LINE LIFF ได้'
        lastInitError = msg
        throw new LiffInitError(
          `${msg} — ตรวจสอบ LIFF Endpoint URL ใน LINE Developers ให้ตรงกับ URL ที่เปิดจริง (รวม path /liff)`,
        )
      })
  }

  await initPromise
}

export function resetLiffInit(): void {
  initPromise = null
  lastInitError = null
}

export function isLiffInClient(): boolean {
  try {
    return liff.isInClient()
  } catch {
    return false
  }
}

export function isLiffLoggedIn(): boolean {
  try {
    return liff.isLoggedIn()
  } catch {
    return false
  }
}

/** Triggers LINE login redirect when inside LINE app and not logged in. */
export function ensureLiffLoggedIn(): void {
  if (!getLiffId()) return
  if (isLiffInClient() && !isLiffLoggedIn()) {
    liff.login({ redirectUri: window.location.href })
  }
}

export async function getLiffProfile(): Promise<LiffProfile> {
  await initLiff()

  if (!isLiffLoggedIn()) {
    if (isLiffInClient()) {
      ensureLiffLoggedIn()
    }
    throw new Error('LIFF_NOT_LOGGED_IN')
  }

  const profile = await liff.getProfile()
  let idToken: string | undefined
  try {
    idToken = liff.getIDToken() ?? undefined
  } catch {
    idToken = undefined
  }
  return {
    userId: profile.userId,
    displayName: profile.displayName,
    pictureUrl: profile.pictureUrl,
    idToken,
  }
}

/** Dev fallback when not opened inside LINE. */
export function getDevLineUserId(): string {
  return import.meta.env.VITE_LIFF_DEV_LINE_USER_ID ?? 'dev-line-user'
}

export async function resolveLiffProfile(): Promise<LiffProfile> {
  const liffId = getLiffId()
  if (!liffId) {
    return {
      userId: getDevLineUserId(),
      displayName: 'Dev User',
    }
  }
  return getLiffProfile()
}
